import { useEffect, useRef } from 'react'

function pathFromHref(href: string): string | null {
  try {
    const trimmed = href.trim()
    // Bootstrap tab links use hash hrefs; these are not app route changes.
    if (trimmed === '' || trimmed === '#' || trimmed.startsWith('#')) {
      return null
    }
    if (trimmed.startsWith('/')) {
      return trimmed.split('?')[0]?.split('#')[0] ?? trimmed
    }
    const url = new URL(trimmed, window.location.href)
    return url.pathname
  } catch {
    return null
  }
}

function isLeavingChartingRoute(
  currentPath: string,
  nextPath: string,
): boolean {
  return (
    currentPath.startsWith('/charting') && !nextPath.startsWith('/charting')
  )
}

/** Intercepts in-app link clicks that leave /charting (BrowserRouter has no history.block). */
export function useChartingLeaveClickGuard(
  getShouldBlock: () => boolean,
  onBlocked: (targetPath: string) => void,
): void {
  const getShouldBlockRef = useRef(getShouldBlock)
  const onBlockedRef = useRef(onBlocked)
  getShouldBlockRef.current = getShouldBlock
  onBlockedRef.current = onBlocked

  useEffect(() => {
    const handleCaptureClick = (event: MouseEvent): void => {
      if (!getShouldBlockRef.current()) {
        return
      }
      if (event.defaultPrevented || event.button !== 0) {
        return
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }
      const anchor = target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement)) {
        return
      }
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return
      }
      const href = anchor.getAttribute('href')
      if (!href) {
        return
      }
      const nextPath = pathFromHref(href)
      if (!nextPath) {
        return
      }
      const currentPath = window.location.pathname
      if (!isLeavingChartingRoute(currentPath, nextPath)) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      onBlockedRef.current(nextPath)
    }
    document.addEventListener('click', handleCaptureClick, true)
    return () => {
      document.removeEventListener('click', handleCaptureClick, true)
    }
  }, [])
}

/** Warn on tab close / refresh when charting has unsaved changes. */
export function useChartingBeforeUnload(getShouldBlock: () => boolean): void {
  const getShouldBlockRef = useRef(getShouldBlock)
  getShouldBlockRef.current = getShouldBlock

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!getShouldBlockRef.current()) {
        return
      }
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])
}
